/**
 * Iter 164 · Shared QuickAddTask popover.
 * Reused across all 7 task views. Parent passes:
 *   • wsId
 *   • projectId (optional — pre-fills)
 *   • preset (optional — { status, due_date, project_id, assignee_id, priority })
 *   • onCreated(task) — callback after successful POST
 */
import { cloneElement, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SearchableSelect from "@/components/ui/SearchableSelect";
import RecurrenceEditor from "./RecurrenceEditor";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Sparkles, FileText } from "lucide-react";
import SmartRichText from "@/components/SmartRichText";

const STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

export function QuickAddTaskInline({ wsId, preset = {}, projects = [],
                                       members = [], onCreated,
                                       packageLocked = false, packageName = "",
                                       testid = "quick-add-inline",
                                       placeholder = "+ New task…",
                                       /* Feb 2026 · When true, stacks the title row and the
                                        * project picker + submit button on separate rows so narrow
                                        * Kanban columns stop overlapping. */
                                       stacked = false }) {
  const [title, setTitle] = useState("");
  const [projectId, setProjectId] = useState(preset.project_id || "");
  const [busy, setBusy] = useState(false);

  // Feb 2026 · Package-locked projects — ad-hoc tasks are forbidden;
  // customer must create tasks off a content-calendar deliverable so
  // the SoW audit trail stays intact. We short-circuit here (rather
  // than let the POST fail) to save a network round-trip and to
  // provide a clearer call-to-action right in the row.
  if (packageLocked) {
    return (
      <div data-testid={`${testid}-locked`}
            className="flex items-center gap-2 px-2 py-1.5 border border-dashed border-amber-400 bg-amber-50 text-[11px] text-amber-900">
        <span className="font-semibold">📅 Package-run project</span>
        <span className="opacity-80">
          {packageName ? `Bound to package "${packageName}". ` : ""}
          Add tasks from the content calendar deliverable, not here.
        </span>
      </div>
    );
  }

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/tasks`, {
        title: title.trim(),
        priority: preset.priority || "medium",
        status: preset.status || "open",
        due_date: preset.due_date || null,
        start_date: preset.start_date || null,
        // R66 · Prefer the inline picker over any preset so a user can
        // override "no project" scope without opening the popover.
        project_id: projectId || preset.project_id || null,
        assignee_id: preset.assignee_id || null,
        tags: preset.tags || [],
      });
      toast.success(data.client_name || data.brand_name
        ? `Task added · inherited ${[data.client_name, data.brand_name].filter(Boolean).join(" · ")}`
        : "Task added");
      setTitle("");
      if (onCreated) onCreated(data);
    } catch (e) {
      // Feb 2026 · Package-locked project — surface a shortcut toast
      // to the content calendar so admins pivot in one click.
      const det = e?.response?.data?.detail;
      if (det?.error === "package_locked_project" && det?.project_id) {
        toast.error(det.message || "This project is locked to a package.", {
          action: {
            label: "Take me to the calendar",
            onClick: () => {
              window.location.href = `/app/content-calendar?project_id=${det.project_id}`;
            },
          },
          duration: 10000,
        });
      } else {
        toast.error(apiErrorMessage(e, "Couldn't add task"));
      }
    }
    finally { setBusy(false); }
  };

  // Show the compact project picker only when the caller didn't already
  // scope the row to a project AND there are projects to pick from. Keeps
  // the inline row minimal in project-scoped views (kanban of one project,
  // project timeline, etc.).
  const showProjectPicker = !preset.project_id && projects.length > 0;

  return (
    <div className={stacked
                     ? "flex flex-col gap-1"
                     : "flex items-center gap-1"} data-testid={testid}>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
        placeholder={placeholder}
        disabled={busy}
        className={stacked
                     ? "h-8 text-xs rounded-none w-full min-w-0"
                     : "h-8 text-xs rounded-none flex-1 min-w-0"}
        data-testid={`${testid}-input`}
      />
      {stacked ? (
        <div className="flex items-center gap-1 w-full">
          {showProjectPicker && (
            <SearchableSelect
              value={projectId || "_none"}
              onValueChange={(v) => setProjectId(v === "_none" ? "" : v)}
              options={[
                { value: "_none", label: "No project" },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
              ]}
              placeholder="Project…"
              triggerClassName="h-8 rounded-none text-xs flex-1 min-w-0"
              width="220px"
              data-testid={`${testid}-project`}
            />
          )}
          <Button size="sm" variant="outline" disabled={busy || !title.trim()}
                   onClick={submit} className="rounded-none h-8 px-2 shrink-0"
                   data-testid={`${testid}-submit`}>
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      ) : (
        <>
          {showProjectPicker && (
            <SearchableSelect
              value={projectId || "_none"}
              onValueChange={(v) => setProjectId(v === "_none" ? "" : v)}
              options={[
                { value: "_none", label: "No project" },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
              ]}
              placeholder="Project…"
              triggerClassName="h-8 rounded-none text-xs w-32 shrink-0"
              width="220px"
              data-testid={`${testid}-project`}
            />
          )}
          <Button size="sm" variant="outline" disabled={busy || !title.trim()}
                   onClick={submit} className="rounded-none h-8 px-2 shrink-0"
                   data-testid={`${testid}-submit`}>
            <Plus className="h-3 w-3" />
          </Button>
        </>
      )}
    </div>
  );
}


export function QuickAddTaskPopover({ wsId, preset = {}, projects = [],
                                        members = [], onCreated, trigger,
                                        defaultOpen = false,
                                        onOpenChange,
                                        /* Feb 2026 R293zze · Show the triple-action footer
                                         * (Save & Close / Save & Go to Tasks / Save & Open
                                         * Task) instead of the default single "Add" button.
                                         * Each matching callback is invoked with the saved
                                         * task object; defaults are no-ops. */
                                        showActionChoices = false,
                                        onSaveAndClose,
                                        onSaveAndGotoTasks,
                                        onSaveAndOpenTask,
                                        onSaveAndAnother,
                                        testid = "quick-add-popover" }) {
  const [open, setOpen] = useState(defaultOpen);
  const setOpenAndNotify = (v) => { setOpen(v); onOpenChange?.(v); };
  // R148k · Feb 2026 — Header "Create → task" nav bug.
  // `defaultOpen` is only read by useState on the first mount. When the
  // Tasks page is already mounted (React Router keeps it alive) and the
  // user hits the Create dropdown again, `?new=1` flips defaultOpen to
  // true but the popover state stays stale. Sync it on every change so
  // the popover actually opens the moment the flag flips.
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    status: preset.status || "open",
    priority: preset.priority || "medium",
    due_date: preset.due_date || "",
    project_id: preset.project_id || "",
    assignee_id: preset.assignee_id || "",
    recurrence: null,
    recurrence_mode: "on_completion",
  });
  const [busy, setBusy] = useState(false);
  // Feb 2026 · Collapsible description field so the Quick Add stays
  // compact by default — user can expand to add a rich-text body
  // without opening the task detail panel.
  const [showDescription, setShowDescription] = useState(false);
  // Iter 253z-4 · Template dropdown lets users spawn from a saved template
  // right inside Quick Add. Loaded lazily when the popover opens.
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const loadTemplates = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/task-templates`);
      setTemplates(r.data || []);
    } catch { setTemplates([]); }
  };
  useEffect(() => { if (open) loadTemplates(); /* eslint-disable-next-line */ }, [open, wsId]);
  const onPickTemplate = (id) => {
    setTplId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({
      ...f,
      title: t.title_template || f.title,
      priority: t.priority || f.priority,
      assignee_id: t.default_assignee_id || f.assignee_id,
    }));
  };

  const submit = async (postAction = null) => {
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    setBusy(true);
    try {
      let data;
      if (tplId) {
        // Spawn from template so checklist + est_hours + description carry over.
        const r = await api.post(`/task-templates/${tplId}/spawn`, {
          project_id: form.project_id || null,
          assignee_id: form.assignee_id || null,
          due_date: form.due_date || null,
          title_override: form.title.trim(),
        });
        data = r.data?.task || r.data;
      } else {
        const r = await api.post(`/workspaces/${wsId}/tasks`, {
          title: form.title.trim(),
          description: form.description || null,
          status: form.status,
          priority: form.priority,
          due_date: form.due_date || null,
          project_id: form.project_id || null,
          assignee_id: form.assignee_id || null,
          recurrence: form.recurrence || null,
          recurrence_mode: form.recurrence ? form.recurrence_mode : null,
        });
        data = r.data;
      }
      toast.success(tplId
        ? "Task created from template"
        : (data.client_name || data.brand_name)
          ? `Task added · inherited ${[data.client_name, data.brand_name].filter(Boolean).join(" · ")}`
          : "Task added");
      // Feb 2026 R293zze · Reset only the per-task fields (title +
      // description + template pick) so repeat entry keeps the
      // "scope" dropdowns (project, assignee, status, priority, due
      // date, recurrence) steady between back-to-back saves.
      setForm({ ...form, title: "", description: "" }); setTplId("");
      setShowDescription(false);
      // Keep the dialog open when the caller asked for "Save & Add
      // Another" — all other post-actions close it as before.
      if (postAction !== "another") setOpenAndNotify(false);
      if (onCreated) onCreated(data);
      // Feb 2026 R293zze · Triple-action post-save hooks. Caller decides
      // what happens next: stay (close), navigate to the Tasks list, or
      // open the task detail panel.
      if (postAction === "close") onSaveAndClose?.(data);
      else if (postAction === "goto") onSaveAndGotoTasks?.(data);
      else if (postAction === "open") onSaveAndOpenTask?.(data);
      else if (postAction === "another") onSaveAndAnother?.(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't add task")); }
    finally { setBusy(false); }
  };

  // R68 · When no visible trigger is provided (e.g. row-scoped Add-task
  // from the Projects list opens the popover programmatically), fall
  // back to a centered Dialog so the popup lands in the middle of the
  // screen with a proper backdrop — previously the hidden anchor made
  // Radix position the popover at (0,0) top-left of the viewport.
  const usesDialogFallback = !trigger;

  const formBody = (
        <div className="space-y-2.5">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Sparkles className="h-3 w-3" /> Quick task
          </div>
          {templates.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Use template (optional)</Label>
              <Select value={tplId || "_none"} onValueChange={(v) => onPickTemplate(v === "_none" ? "" : v)}>
                <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-template`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— No template —</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Input
            autoFocus
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter" && e.metaKey) submit(); }}
            placeholder="What needs to be done?"
            data-testid={`${testid}-title`}
          />
          {/* Feb 2026 · Collapsible rich-text description. Keeps the
              Quick Add tight by default; expands on click so users can
              add a body without opening the full task detail panel. */}
          {!showDescription ? (
            <button
              type="button"
              onClick={() => setShowDescription(true)}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              data-testid={`${testid}-add-description`}
            >
              <FileText className="h-3 w-3" /> Add description
              {form.description ? (
                <span className="ml-1 text-[10px] text-emerald-700 dark:text-emerald-300">· saved draft</span>
              ) : null}
            </button>
          ) : (
            <div data-testid={`${testid}-description-wrap`}>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Description</Label>
                <button
                  type="button"
                  onClick={() => setShowDescription(false)}
                  className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  data-testid={`${testid}-hide-description`}
                >
                  Hide
                </button>
              </div>
              <SmartRichText
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                minRows={3}
                maxHeight={180}
                mediaMode="url-only"
                placeholder="Add context, checklists, links…"
                testid={`${testid}-description`}
              />
            </div>
          )}
          {/* Iter · Feb 2026 — On mobile the 2-col grid squeezes the native
              `<input type="date">` down to a tiny sliver on iOS Safari,
              which makes it look like the Due-date input is missing and
              the Assignee dropdown looks like it's overlapping it.
              Stack single-column below 640px, keep 2-col from `sm:` up. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-3 [&_label]:mb-1 [&_label]:block">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-status`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid={`${testid}-priority`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Due date</Label>
              <Input type="date" value={form.due_date}
                     onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                     className="h-9 text-xs w-full min-w-0"
                     data-testid={`${testid}-due`} />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Assignee</Label>
              <SearchableSelect
                value={form.assignee_id || "_none"}
                onValueChange={(v) => setForm({ ...form, assignee_id: v === "_none" ? "" : v })}
                options={[
                  { value: "_none", label: "Unassigned" },
                  // R148k · Feb 2026 — Names only (no email hint), sorted A→Z,
                  // filter out members without a resolved user_id.
                  ...(members || [])
                    .map((m) => ({
                      value: m.user_id || m.id,
                      label: m.user?.name || m.name || "Unknown",
                    }))
                    .filter((o) => o.value)
                    .sort((a, b) => String(a.label).localeCompare(String(b.label))),
                ]}
                placeholder="Search a teammate…"
                sort="none"
                triggerClassName="h-9 text-xs w-full"
                width="100%"
                data-testid={`${testid}-assignee`}
              />
              {/* Feb 2026 · Department of the chosen assignee —
                  informational only. Resolved locally from the
                  workspace members list (which the backend enriches
                  with the user's primary department). */}
              {form.assignee_id && (() => {
                const dept = (members || []).find(
                  (m) => (m.user_id || m.id) === form.assignee_id)?.department_name || "";
                return (
                  <div className="mt-1 text-[10px] flex items-center gap-1.5"
                        data-testid={`${testid}-assignee-department`}>
                    <span className="uppercase tracking-widest text-muted-foreground">Department</span>
                    <span className={dept ? "font-medium text-foreground" : "italic text-muted-foreground"}>
                      {dept || "—"}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
          {/* R73d · Always render the Project picker so it's visible &
              preselected when preset.project_id is passed. Directors want
              to CONFIRM (or override) the project on the card they clicked. */}
          {projects.length > 0 && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Project</Label>
              <SearchableSelect
                value={form.project_id || "_none"}
                onValueChange={(v) => setForm({ ...form, project_id: v === "_none" ? "" : v })}
                options={[
                  { value: "_none", label: "No project" },
                  ...projects.map((p) => ({ value: p.id, label: p.name })),
                ]}
                placeholder="Select project…"
                triggerClassName="h-8 text-xs w-full"
                width="100%"
                data-testid={`${testid}-project`}
              />
            </div>
          )}
          <RecurrenceEditor
            value={form.recurrence}
            mode={form.recurrence_mode}
            onChange={(rule, m) => setForm({ ...form, recurrence: rule, recurrence_mode: m })}
            testid={`${testid}-recurrence`}
          />
          <div className="flex justify-end flex-wrap gap-1 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpenAndNotify(false)}>Cancel</Button>
            {showActionChoices ? (
              <>
                <Button size="sm" variant="outline" onClick={() => submit("another")} disabled={busy}
                         title="Save, keep the project/assignee/priority dropdowns and clear the title so you can add the next one"
                         data-testid={`${testid}-save-another`}>
                  {busy ? "Saving…" : "Save & Add Another"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => submit("close")} disabled={busy}
                         data-testid={`${testid}-save-close`}>
                  {busy ? "Saving…" : "Save & Close"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => submit("goto")} disabled={busy}
                         data-testid={`${testid}-save-goto`}>
                  {busy ? "Saving…" : "Save & Go to Tasks"}
                </Button>
                <Button size="sm" onClick={() => submit("open")} disabled={busy}
                         data-testid={`${testid}-save-open`}>
                  {busy ? "Saving…" : "Save & Open Task"}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => submit()} disabled={busy}
                       data-testid={`${testid}-save`}>
                {busy ? "Saving…" : "Add"}
              </Button>
            )}
          </div>
        </div>
  );

  if (usesDialogFallback) {
    // Feb 2026 R100 · The Dialog fallback used to omit any visible trigger,
    // leaving pages that relied on a "Quick add" button (Tasks list,
    // Projects > Tasks) without a way to open the create form. Render a
    // default button when no `trigger` is passed so the CTA is visible;
    // the caller can still supply a custom trigger via the prop.
    //
    // Feb 2026 R293p · Honour the caller-supplied `trigger` on the
    // Dialog fallback path too so an icon-only pill (used by the
    // Project > Tasks compact toolbar) keeps working on mobile.
    return (
      <>
        {trigger
          ? cloneElement(trigger, {
              onClick: (e) => {
                try { trigger.props?.onClick?.(e); } catch { /* noop */ }
                setOpenAndNotify(true);
              },
            })
          : (
            <Button size="sm" data-testid={testid} onClick={() => setOpenAndNotify(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Quick add
            </Button>
          )
        }
        <Dialog open={open} onOpenChange={setOpenAndNotify}>
          <DialogContent className={`${showActionChoices ? "max-w-2xl" : "max-w-lg"} rounded-none`} data-testid={`${testid}-content`}>
            <DialogHeader>
              <DialogTitle className="text-sm">Add a task{preset.project_id ? " · scoped to this project" : ""}</DialogTitle>
            </DialogHeader>
            {formBody}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpenAndNotify}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button size="sm" data-testid={testid}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Quick add
          </Button>
        )}
      </PopoverTrigger>
      {/* R159j · Feb 2026 — Mobile-first positioning. On phones, the
          Radix Popover portals BELOW the trigger which frequently lands
          off-screen when the "Add task" button sits near the bottom of
          a project card. Force fixed viewport-centred positioning on
          < md via a class hook so the popover always opens in view
          and can scroll internally if content exceeds the viewport. */}
      <PopoverContent
        className={`${showDescription ? "w-96" : "w-80"} quick-add-popover-content`}
        align="start"
        sideOffset={6}
        collisionPadding={16}
        data-testid={`${testid}-content`}
      >
        {formBody}
      </PopoverContent>
    </Popover>
  );
}
